import React, { ReactNode } from 'react'
import { Indicator } from 'is24-corecss'
import { Trans, useTranslation } from 'react-i18next'
import { IconS24BuildingMagnifier24, IconS24UsersThree24 } from '@is24/cosma-ui-icons'
import styles from './SupplyDemandTiles.module.css'

type SupplyDemandTilesProps = {
  savedSearches?: number
  similarObjects?: number
}

type TileProps = {
  title: string
  description: ReactNode
  indicatorLabel: string
  icon: ReactNode
}

const getSavedSearchesLevel = (savedSearches: number) => {
  if (savedSearches >= 50) {
    return 'high'
  }

  if (savedSearches >= 15) {
    return 'medium'
  }

  return 'low'
}

const getSimilarObjectsLevel = (similarObjects: number) => {
  if (similarObjects >= 25) {
    return 'high'
  }

  if (similarObjects >= 5) {
    return 'medium'
  }

  return 'low'
}

function SupplyDemandTile({ title, description, indicatorLabel, icon }: TileProps) {
  return (
    <div className={styles.tile}>
      <div className={styles.tileIcon}>{icon}</div>
      <p className={`font-body-medium-bold ${styles.tileTitle}`}>{title}</p>
      <p className={`font-body-medium-regular ${styles.tileDescription}`}>{description}</p>
      <Indicator.Root appearance='label' color='blue' variant='flat' className={styles.tileIndicator}>
        {indicatorLabel}
      </Indicator.Root>
    </div>
  )
}

export function SupplyDemandTiles({ savedSearches, similarObjects }: SupplyDemandTilesProps) {
  const { t, i18n } = useTranslation()

  if (savedSearches === undefined || similarObjects === undefined) {
    return null
  }

  const formatNumber = (value: number) => value.toLocaleString(i18n.language)
  const savedSearchesLevel = getSavedSearchesLevel(savedSearches)
  const similarObjectsLevel = getSimilarObjectsLevel(similarObjects)

  return (
    <div className={styles.tiles}>
      <SupplyDemandTile
        icon={<IconS24BuildingMagnifier24 />}
        title={t('wizard.screen.basicInfo.price.recommendedNotification.check.supplyDemand.supply.title')}
        description={
          <Trans
            i18nKey='wizard.screen.basicInfo.price.recommendedNotification.check.supplyDemand.supply.description'
            values={{ formattedCount: formatNumber(similarObjects) }}
            components={{ strong: <strong /> }}
          />
        }
        indicatorLabel={t(
          `wizard.screen.basicInfo.price.recommendedNotification.check.supplyDemand.supply.indicator.${similarObjectsLevel}`
        )}
      />
      <SupplyDemandTile
        icon={<IconS24UsersThree24 />}
        title={t('wizard.screen.basicInfo.price.recommendedNotification.check.supplyDemand.demand.title')}
        description={
          <Trans
            i18nKey='wizard.screen.basicInfo.price.recommendedNotification.check.supplyDemand.demand.description'
            values={{ formattedCount: formatNumber(savedSearches) }}
            components={{ strong: <strong /> }}
          />
        }
        indicatorLabel={t(
          `wizard.screen.basicInfo.price.recommendedNotification.check.supplyDemand.demand.indicator.${savedSearchesLevel}`
        )}
      />
    </div>
  )
}

export const SupplyDemandTilesTestExports = {
  getSavedSearchesLevel,
  getSimilarObjectsLevel,
}
